import {BaseService} from './base.service';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {catchError, finalize, ignoreElements, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import {Store} from '@ngrx/store';
import {LocalComponentStore} from '../store/local-component.store';
import {AppSettings} from '../app.settings';
import {ToastrService} from 'ngx-toastr';

import { each } from 'lodash-es';
import {CacheModel} from '../models/cache.model';
import {Response} from '../models/response.model';
import {Product} from '../models/product.model';
import {ProductCatalog} from '../models/product-catalog.model';
import {FilterGroup} from '../models/filter-group.model';
import {HeroSlide} from '../interfaces/hero-slide';
import {OrderWindow} from '../models/order-window.model';
import {ApiProductService, ProductResponseShape, ProductStatistics} from './api-product.service';
import {ProductResult} from '../interfaces/product.result';
import {CAROUSEL_TYPE, ProductView} from '../constants/product-type';
import {GoogleAnalytics} from './google-analytics.service';
import {IDecisionPointData} from '../interfaces/decision-point.data';
import {floorLastCent, computeHash, isEmptyArray, transformFilters} from '../shared/utils';
import {OrderItem} from '../models/order-item.model';
import {IDecisionPointDelivery} from '../interfaces/decision-point-delivery';
import {PRODUCT_ACCESS_ERROR} from '../constants/error.codes';
import {ProductCategory} from '../models/product-category.model';
import {Brand} from '../models/brand.model';
import { IRunOnPrice, IPriceTiers, ITierInfo } from '../interfaces/price-tiers';
import {convertCatalogsToHeroSlides, DetailedError} from '../shared/helpers';
import {CurrencyService} from './currency.service';
import {API_URL} from '../constants/api-urls';
import { SKU } from '../models/sku.model';
import {Order} from '../models/order.model';
import {AllocationGroup, InventoryAllocation} from '../interfaces/allocation-group';
import {IAppState} from '../store/app.store';
import {
  fetchUserAllocationGroups,
  fetchUserAllocationGroupsSuccess,
  fetchUserAllocationGroupsFailure,
  fetchProductAllocations,
  fetchProductAllocationsSuccess,
  fetchProductAllocationsFailure,
  fetchProductsAllocations,
  fetchProductsAllocationsSuccess,
  fetchProductsAllocationsFailure,
  resetProductAllocations,
} from '../store/actions/inventory-allocations.actions';
import {
  selectUserAllocationGroups,
  selectUserAllocationGroupsLoaded,
  selectInventoryAllocationForSku,
  selectAllProductAllocationsDefined,
  selectTotalAvailableQuantity,
  selectSomeVariationsHasAllocationGroups,
  selectVariationHasInventoryAllocations,
  selectIsInventoryAllocationOutOfStock,
  selectIsProductInventoryAllocationOutOfStock,
  selectIsFetchingProductAllocations,
  selectProductAllocationsLoaded,
  selectProductIdsNeedingAllocations,
  selectProductAllocationsFailed,
} from '../store/selectors/inventory-allocations.selectors';

// Shared paging for the per-window "programs" catalog list. onActiveOrderWindowChanged and the
// header-navigation menu must request this same value so the in-flight dedup / id cache collapses
// them into one catalogs/{id} + catalogs/for-ids round-trip. '50-0' = "all programs for the window";
// homepage carousels slice 4-8 from the result, so widening from the old '18-0' changes no display.
export const PROGRAMS_PAGING = '50-0';

type FavoriteProductsCache = Record<number, {products: Product[]; count: number}>;

interface ProductState {
    statistics: ProductStatistics;
    fetchingCatalogsForBuyingWindow: boolean;
    fetchingCatalogsForOnDemand: boolean;
    fetchingCatalogsForBuyingWindowHomepage: boolean;
    fetchingCatalogsForOnDemandHomepage: boolean;
    fetchingPopularOnDemandProducts: boolean;
    fetchingOnDemandProductCategories: boolean;
    fetchingOnDemandProductBrands: boolean;
    fetchingRecentPurchasedProducts: boolean;
    fetchingFavoriteProducts: boolean;
    onDemandCatalogs: ProductCatalog[];
    buyingWindowCatalogs: ProductCatalog[];
    onDemandHomepageCatalogs: ProductCatalog[];
    buyingWindowHomepageCatalogs: ProductCatalog[];
    onDemandPopularProducts: Product[];
    onDemandRecentPurchasesProducts: Product[];
    onDemandRecentPurchasesProductsTotalCount: number;
    onDemandProductCategories: ProductCategory[];
    onDemandProductBrands: Brand[];
    favoriteProducts: FavoriteProductsCache;
}

interface GetProductsByIdsOptions {
    force?: boolean;
    shape?: ProductResponseShape;
}

/**
 * The products whose allocation groups are worth asking for: on demand items that draw their
 * quantity from an allocation group. A buying window item takes its group from the warehouse
 * column instead, and an item without allocated inventory has none to take.
 */
function allocatedInventoryProducts(products: Product[]): Product[] {
    return (products || []).filter(product => product?.id
        && product.isBuyingWindow === false
        && product.isEnabledAllocatedInventory === true);
}

@Injectable()
export class ProductService extends BaseService {

    readonly apiV2URL: string;
    public catalogCache: CacheModel;
    public productCache: CacheModel;
    // protected filterCache: CacheModel;
    protected statCache: CacheModel;
    protected dpCache: CacheModel;

    private favoriteFiltersCache: CacheModel;

    // In-flight catalog requests keyed by the catalog cache key. De-duplicates concurrent
    // fetches (e.g. getCatalogs + getCatalogsForHomepage in onActiveOrderWindowChanged) so the
    // same window/filter/paging/sort triggers a single catalogs/{id} + catalogs/for-ids round-trip.
    private inflightCatalogs = new Map<string, Observable<{catalogs: ProductCatalog[]; count: number}>>();

    protected statFetching = false;

    // state methods
    public currentCartFilters: Record<number, FilterGroup[]> = {};

    // plain (non-observable) caches — unchanged
    productCatalogs: Record<number, ProductCatalog> = {};
    products: Record<number, Product> = {};
    // For-ids brief/full shape only — getProductsByIds({shape: 'brief'}).
    briefProducts: Record<number, Product> = {};
    // Short-info shape only — keep separate: its SKU fields are incompatible with briefProducts.
    shortInfoProducts: Record<number, Product> = {};

    private componentStore = new LocalComponentStore<ProductState>({
        statistics: {},
        fetchingCatalogsForBuyingWindow: false,
        fetchingCatalogsForOnDemand: false,
        fetchingCatalogsForBuyingWindowHomepage: false,
        fetchingCatalogsForOnDemandHomepage: false,
        fetchingPopularOnDemandProducts: false,
        fetchingOnDemandProductCategories: false,
        fetchingOnDemandProductBrands: false,
        fetchingRecentPurchasedProducts: false,
        fetchingFavoriteProducts: false,
        onDemandCatalogs: [],
        buyingWindowCatalogs: [],
        onDemandHomepageCatalogs: [],   // always sorted by is_featured DESC
        buyingWindowHomepageCatalogs: [], // always sorted by is_featured DESC
        onDemandPopularProducts: [],
        onDemandRecentPurchasesProducts: [],
        onDemandRecentPurchasesProductsTotalCount: 0,
        onDemandProductCategories: [],
        onDemandProductBrands: [],
        favoriteProducts: {},
    });


    /** Synchronous accessors preserve the previous public observable fields. */
    get statistics(): ProductStatistics { return this.componentStore.snapshot(s => s.statistics); }
    set statistics(v: ProductStatistics) { this.componentStore.patchState({ statistics: v }); }

    get fetchingCatalogsForBuyingWindow(): boolean { return this.componentStore.snapshot(s => s.fetchingCatalogsForBuyingWindow); }
    set fetchingCatalogsForBuyingWindow(v: boolean) { this.componentStore.patchState({ fetchingCatalogsForBuyingWindow: v }); }

    get fetchingCatalogsForOnDemand(): boolean { return this.componentStore.snapshot(s => s.fetchingCatalogsForOnDemand); }
    set fetchingCatalogsForOnDemand(v: boolean) { this.componentStore.patchState({ fetchingCatalogsForOnDemand: v }); }

    get fetchingCatalogsForBuyingWindowHomepage(): boolean { return this.componentStore.snapshot(s => s.fetchingCatalogsForBuyingWindowHomepage); }
    set fetchingCatalogsForBuyingWindowHomepage(v: boolean) { this.componentStore.patchState({ fetchingCatalogsForBuyingWindowHomepage: v }); }

    get fetchingCatalogsForOnDemandHomepage(): boolean { return this.componentStore.snapshot(s => s.fetchingCatalogsForOnDemandHomepage); }
    set fetchingCatalogsForOnDemandHomepage(v: boolean) { this.componentStore.patchState({ fetchingCatalogsForOnDemandHomepage: v }); }

    get fetchingPopularOnDemandProducts(): boolean { return this.componentStore.snapshot(s => s.fetchingPopularOnDemandProducts); }
    set fetchingPopularOnDemandProducts(v: boolean) { this.componentStore.patchState({ fetchingPopularOnDemandProducts: v }); }

    get fetchingOnDemandProductCategories(): boolean { return this.componentStore.snapshot(s => s.fetchingOnDemandProductCategories); }
    set fetchingOnDemandProductCategories(v: boolean) { this.componentStore.patchState({ fetchingOnDemandProductCategories: v }); }

    get fetchingOnDemandProductBrands(): boolean { return this.componentStore.snapshot(s => s.fetchingOnDemandProductBrands); }
    set fetchingOnDemandProductBrands(v: boolean) { this.componentStore.patchState({ fetchingOnDemandProductBrands: v }); }

    get fetchingRecentPurchasedProducts(): boolean { return this.componentStore.snapshot(s => s.fetchingRecentPurchasedProducts); }
    set fetchingRecentPurchasedProducts(v: boolean) { this.componentStore.patchState({ fetchingRecentPurchasedProducts: v }); }

    get fetchingFavoriteProducts(): boolean { return this.componentStore.snapshot(s => s.fetchingFavoriteProducts); }
    set fetchingFavoriteProducts(v: boolean) { this.componentStore.patchState({ fetchingFavoriteProducts: v }); }

    get onDemandCatalogs(): ProductCatalog[] { return this.componentStore.snapshot(s => s.onDemandCatalogs); }
    set onDemandCatalogs(v: ProductCatalog[]) { this.componentStore.patchState({ onDemandCatalogs: v }); }

    get buyingWindowCatalogs(): ProductCatalog[] { return this.componentStore.snapshot(s => s.buyingWindowCatalogs); }
    set buyingWindowCatalogs(v: ProductCatalog[]) { this.componentStore.patchState({ buyingWindowCatalogs: v }); }

    get onDemandHomepageCatalogs(): ProductCatalog[] { return this.componentStore.snapshot(s => s.onDemandHomepageCatalogs); }
    set onDemandHomepageCatalogs(v: ProductCatalog[]) { this.componentStore.patchState({ onDemandHomepageCatalogs: v }); }

    get buyingWindowHomepageCatalogs(): ProductCatalog[] { return this.componentStore.snapshot(s => s.buyingWindowHomepageCatalogs); }
    set buyingWindowHomepageCatalogs(v: ProductCatalog[]) { this.componentStore.patchState({ buyingWindowHomepageCatalogs: v }); }

    get onDemandPopularProducts(): Product[] { return this.componentStore.snapshot(s => s.onDemandPopularProducts); }
    set onDemandPopularProducts(v: Product[]) { this.componentStore.patchState({ onDemandPopularProducts: v }); }

    get onDemandRecentPurchasesProducts(): Product[] { return this.componentStore.snapshot(s => s.onDemandRecentPurchasesProducts); }
    set onDemandRecentPurchasesProducts(v: Product[]) { this.componentStore.patchState({ onDemandRecentPurchasesProducts: v }); }

    get onDemandRecentPurchasesProductsTotalCount(): number { return this.componentStore.snapshot(s => s.onDemandRecentPurchasesProductsTotalCount); }
    set onDemandRecentPurchasesProductsTotalCount(v: number) { this.componentStore.patchState({ onDemandRecentPurchasesProductsTotalCount: v }); }

    get onDemandProductCategories(): ProductCategory[] { return this.componentStore.snapshot(s => s.onDemandProductCategories); }
    set onDemandProductCategories(v: ProductCategory[]) { this.componentStore.patchState({ onDemandProductCategories: v }); }

    get onDemandProductBrands(): Brand[] { return this.componentStore.snapshot(s => s.onDemandProductBrands); }
    set onDemandProductBrands(v: Brand[]) { this.componentStore.patchState({ onDemandProductBrands: v }); }

    get favoriteProducts(): FavoriteProductsCache { return this.componentStore.snapshot(s => s.favoriteProducts); }
    set favoriteProducts(v: FavoriteProductsCache) { this.componentStore.patchState({ favoriteProducts: v }); }


    public updatedProduct = new Subject<Product>();
    public updatedBriefProduct = new Subject<Product>();
    // Listing/favourites notifications only — kept separate from updatedBriefProduct.
    public updatedShortInfoProduct = new Subject<Product>();
    public productAddedToCart = new Subject<Product>();

    private activeBuyingWindowId: number;

    // shared between the callers of fetchAllocationGroups() while the request is in flight
    private inFlightAllocationGroups$: Observable<AllocationGroup[]> = null;




    /**
     * returns  discount price or original if discount hasn't been see
     * @param price - original price
     * @param product - product model
     * @param hasTrimesterBudget - if true discount is not applied
     */
    static computeDiscountPrice(price: number, product: Product, hasTrimesterBudget: boolean = false): number {
        if (!product.discount || hasTrimesterBudget) {
            return price;
        }

        return floorLastCent(price * (1 - product.discount / 100) + 0);
    }

    static computePriceTiers(product: Product, sku: SKU, quantity: number): ITierInfo {
        // reset price tiers
        let priceTiers: IPriceTiers[] = [];
        let runOnPrices: IRunOnPrice[] = [];


        if (product.hasPriceRunOnRange) {
            runOnPrices =  sku.getPriceTiers().map( p => {
                  return {
                      quantity: p.quantity,
                      pricing: this.checkPriceType(p.pricing),
                      each_additional:  this.checkPriceType(p.each_additional),
                      selected: false,
                      currency: product.currency,
                      packageType: product.getUOM()
                  };
              }
            );

            this.findSelectedTier(runOnPrices, quantity);

            return{
                priceTiers: [],
                runOnPrices
            }
        }

        const result: Array< IPriceTiers> = [];
        if (!product) {
            return {
                priceTiers: result,
                runOnPrices: []
            }
        }

        const tiers = sku.getPriceTiers();
        if (tiers.length < 2) {
            return{
                priceTiers: result,
                runOnPrices: []
            }
        }

        for ( let i = 0; i < tiers.length; i++) {
            let range: string;
            let selected = false;
            const value = tiers[i].value;
            const key = parseInt(tiers[i].key, 10);
            const alreadySelected = result.some( p => p.selected);
            // const {key, value} = tiers[i];
            if (i === 0) {
                range = `< ${key}`;
                selected = quantity < key;
            } else if ( i < tiers.length - 1) {
                const previousTier = tiers[i - 1];
                const from = parseInt(previousTier.key, 10);
                const to = key - 1;
                if (from !== to) {
                    range = ( from !== to ) ? `${from} - ${to}` : `${to}`;
                    selected = !alreadySelected  && (quantity >= from && quantity <= to);
                } else {
                    range =  `${to}`;
                    selected = !alreadySelected && (quantity === from);
                }
            } else {
                const previousTier = tiers[i - 1];
                range = `>= ${previousTier.key}`;
                selected = quantity >=  parseInt(previousTier.key, 10);
            }
            result.push({ range, price: this.checkPriceType(value), selected, packageType: product.getUOM(),
                currency: product.currency});
        }
        priceTiers = result;

        return {priceTiers, runOnPrices};
    }

    private static checkPriceType(value: string | number): number {
        if (value === undefined || value === null) {
            return 0;
        }

        if (typeof value === 'string') {
            return parseFloat(value);
        }

        return value;
    }

    private static findSelectedTier(runOnRange: IRunOnPrice[], quantity: number) {
        for (let i = runOnRange.length - 1; i >= 0; i--) {
            if (quantity >= runOnRange[i].quantity) {
                runOnRange[i].selected =  true;
                return;
            }

            if (i === 0) {
                // select first tier by default
                runOnRange[i].selected = true;
                return;
            }
        }
    }

    constructor(
        protected http: HttpClient,
        protected appSettings: AppSettings,
        protected toastr: ToastrService,
        protected router: Router,
        private api: ApiProductService,
        private  googleAnalytics: GoogleAnalytics,
        private currencyService: CurrencyService,
        private store: Store<IAppState>,
    ) {
        super('/products', http, appSettings, toastr);
        this.apiV2URL = `${API_URL}/products`;

        this.cache.persistent = true;
        this.cache.prefix = 'Products';

        this.catalogCache = new CacheModel();
        this.productCache = new CacheModel();
        // this.filterCache = new CacheModel();
        // this.filterCache.expiration = 3600;

        this.statCache = new CacheModel();
        this.dpCache = new CacheModel();

        this.favoriteFiltersCache = new CacheModel();

        // fetch on-demand catalogs
        // this.onActiveOrderWindowChanged(0).subscribe( data => {});
    }

    private getStatistics(orderWindowId: number) {
        this.statFetching = true;
        return this.api.getStatistics(orderWindowId).pipe( tap( stats => {
            this.statFetching = false;
            this.setStatistics(stats);
        }));
    }

    getEcomProducts(programId = 1, filter: Array<string> = null, paging: string = '20-0',
                    sort: string = '', wslrEntityId = 0): Observable<ProductResult> {
        return this.getProducts(1, programId, filter, paging, sort, wslrEntityId);
    }

    // Buying Window

    public getCatalog(slugOrID: string | number = null): Observable<ProductCatalog> {
        if (!slugOrID) {
            return of(null);
        }
        return this.api.getProductCatalog(slugOrID).pipe( tap( catalog => {
            this.productCatalogs[catalog.id] = catalog;
        }));
    }

    public getCatalogsForHomepage(windowID = 0, paging = PROGRAMS_PAGING): Observable<{catalogs: ProductCatalog[]}> {
        if (windowID) {
            this.fetchingCatalogsForBuyingWindowHomepage = true;
        } else {
            this.fetchingCatalogsForOnDemandHomepage = true;
        }
        this.setHomepageCatalogs(windowID, []);
        // Share the same in-flight request as getCatalogs (identical params) and only fill the
        // homepage field here — the buying-window field is owned by fetchCatalogs/getCatalogs.
        return this.fetchCatalogsShared(windowID, null, paging).pipe(
            tap (({catalogs}) => {
                this.setHomepageCatalogs(windowID, catalogs);
                this.resetLoadingIndicator();
            })
        );
    }

    public getCatalogs(windowID: number = 0,
                filter: Array<string> = null,
                paging: string = '18-0',
                sort: string = 'is_featured DESC'): Observable<{catalogs: ProductCatalog[]; count: number}> {

        if (windowID) {
            this.fetchingCatalogsForBuyingWindow = true;
        } else {
            this.fetchingCatalogsForOnDemand = true;
        }
        this.setCatalogs(windowID, []);
        return this.fetchCatalogs(windowID, filter, paging, sort);
    }

    resetLoadingIndicator() {
        this.fetchingCatalogsForBuyingWindowHomepage = false;
        this.fetchingCatalogsForOnDemandHomepage = false;
        this.fetchingCatalogsForBuyingWindow = false;
        this.fetchingCatalogsForOnDemand = false;
    }


    private fetchCatalogs(windowID: number = 0,
                          filter: Array<string> = null,
                          paging: string = '18-0',
                          sort: string = 'is_featured DESC'): Observable<{catalogs: ProductCatalog[]; count: number}> {

        return this.fetchCatalogsShared(windowID, filter, paging, sort).pipe(
            tap ( val => {
                this.setCatalogs(windowID, val?.catalogs || []);
                this.resetLoadingIndicator();
            })
        );
    }

    // Builds the catalogs cache/registry key. Kept identical to the legacy key in getCatalogIds so
    // the id cache and the in-flight registry agree on what "the same request" means.
    private catalogsKey(windowID: number, filter: Array<string>, paging: string, sort: string): string {
        return btoa(JSON.stringify({w: windowID, f: filter, p: paging, s: sort})).replace('=', '');
    }

    // Side-effect-free catalog fetch shared across concurrent callers. The returned observable is
    // registered by key while in flight (shareReplay) and removed on completion, so two subscriptions
    // with the same params resolve from one catalogs/{id} + catalogs/for-ids round-trip. MobX field
    // writes stay in the callers (fetchCatalogs / getCatalogsForHomepage).
    private fetchCatalogsShared(windowID: number = 0,
                                filter: Array<string> = null,
                                paging: string = '18-0',
                                sort: string = 'is_featured DESC'): Observable<{catalogs: ProductCatalog[]; count: number}> {

        if (!sort) {
            sort = 'is_featured DESC';
        }

        const key = this.catalogsKey(windowID, filter, paging, sort);
        const existing = this.inflightCatalogs.get(key);
        if (existing) {
            return existing;
        }

        const shared = this.getCatalogIds(windowID, filter, paging, sort).pipe(
            switchMap( ({ids, count}) => this.getCatalogsByIds(ids).pipe(
                map( catalogs => ({catalogs, count}))
            )),
            finalize( () => this.inflightCatalogs.delete(key)),
            shareReplay(1)
        );

        this.inflightCatalogs.set(key, shared);
        return shared;
    }

    public getPopularOnDemandProducts(wslrEntityId = 0): Observable<Product[]> {
        const sort = 'popularity DESC';
        if (this.fetchingPopularOnDemandProducts || this.onDemandPopularProducts.length > 0) {
            return of(this.onDemandPopularProducts);
        }
        this.fetchingPopularOnDemandProducts = true;
        this.onDemandPopularProducts = [];

        return this.getProductsShortInfo(1, 1, 0, null, '20-0', sort, '', wslrEntityId).pipe(
            map( ({products}) => {
                this.onDemandPopularProducts = [...products];
                this.fetchingPopularOnDemandProducts = false;
                return this.onDemandPopularProducts;
            })
        )
    }


    private getCatalogIds(windowID: number = null, filter: Array<string> = null,
                                paging: string = '18-0', sort: string = ''):
        Observable<{ids: number[]; count: number}> {

        const key: string = this.catalogsKey(windowID, filter, paging, sort);
        const catalogIdsKey = `${key}-ids`;
        const catalogIdsCountKey = `${key}-ids-count`;

        if (this.catalogCache.has(catalogIdsKey)) {
            const ids = this.catalogCache.get(catalogIdsKey);
            const count = this.catalogCache.get(catalogIdsCountKey);
            return of({ids, count});
        }

        return this.api.getCatalogsIds(windowID, filter, paging, sort).pipe( tap( ({ids, count}) => {
            this.catalogCache.set(catalogIdsKey, ids);
            this.catalogCache.set(catalogIdsCountKey, count);
        }));

    }

    private getCatalogsByIds( ids: number[]): Observable<ProductCatalog[]> {

        if (!ids || ids.length === 0) {
            return of([]);
        }
        const getCatalogs = (idList) => idList.map( id => this.productCatalogs[id]);

        // find missing catalogs
        const missing = ids.filter( id =>  this.productCatalogs[id] === undefined);
        if (missing.length === 0) {
            return of ( getCatalogs(ids));
        }

        return this.api.getCatalogsByIds(missing).pipe( map(catalogs => {
            catalogs.forEach( catalog => this.productCatalogs[catalog.id] = new ProductCatalog(catalog));
            return getCatalogs(ids);
        }))


    }

    getFilters(groupID: number = null, type: string, filters: FilterGroup[] = []): Observable<FilterGroup[]> {
        type = type + '-filters';

        if (type === 'all-product-filters' && groupID === 0 ) {
            // if active window  is not loaded no filter should be requested
            return of([]);
        }

        if (isEmptyArray(filters)) {

            // const eTag: string = this.cache.getETag(key);
            // const httpOptions: Object = {headers: new HttpHeaders({'If-Match': eTag})};

            // if (this.filterCache.has(key)) {
            //     const items = [];
            //     each(this.cache.getItems(key), (item, i) => {
            //         items[i] = new FilterGroup(item);
            //     });
            //     return of(items);
            // }

            const url = (type === 'product-filters' || type === 'all-product-filters')
                ? `${API_URL}/es/products/catalog/${groupID}/${type}`
                : `${this.apiV2URL}/catalog/${groupID}/${type}`;
            return this.http.get<Response>(url)
                .pipe(
                    map(response => {
                        const result = response.data;

                        each(result, (item, i) => {
                            result[i] = new FilterGroup(item);
                        });

                        return result;
                    }),
                    catchError(this.handleError('ProductService::getFilters', []))
                );

        } else {
            // post request  with filtering
            const filtersString = transformFilters(filters);

            // const key = `${type}-${groupID}-${filterHash}`;
            // if (this.filterCache.has(key)) {
            //     return of(this.cache.getItems(key).map( i => new FilterGroup(i)));
            // }
            const url = (type === 'product-filters' || type === 'all-product-filters')
                ? `${API_URL}/es/products/catalog/${groupID}/${type}`
                : `${this.apiV2URL}/catalog/${groupID}/${type}`;
            return this.http.post<Response>(url, {filter: filtersString})
                .pipe(
                    map(response => {

                        if (!isEmptyArray(response.data)) {
                            return response.data.map( i => new FilterGroup(i));
                        }
                        return [];
                    }),
                    // tap( retFilters => {
                    //     this.filterCache.set(key, true);
                    //     this.cache.setItems(key, retFilters);
                    // }),
                    catchError(this.handleError('ProductService::getFilters', []))
                );

        }

    }

    getProduct(slugOrID: string | number = null, wslrEntityId = 0, isAutoShip = false, force = false): Observable<Product> {

        if (!force) {
            if ( typeof slugOrID === 'string') {
                // slug case
                const cachedProducts: Product[] = Object.values(this.products);
                const foundProduct = cachedProducts.find( p => p.slug === slugOrID);
                if (foundProduct) {
                    return of(foundProduct);
                }

            }

            if ( typeof slugOrID === 'number') {
                // id case
                if (this.products[slugOrID]) {
                    return of(this.products[slugOrID]);
                }
            }
        }

        // let url = `${this.apiURL}${isAutoShip ? '/inactive' : ''}/${slugOrID}`;  old api
        let url = `${this.apiV2URL}${isAutoShip ? '/inactive' : ''}/${slugOrID}`;
        if (wslrEntityId) {
            url += `?entity_id=${wslrEntityId}`;
        }
        return this.http.get<Response>(url)
            .pipe(
                map(response => {
                    // check if redirect_url presents
                    if (response['redirect_url']) {
                        this.router.navigateByUrl(response['redirect_url']);
                        return null;
                    }
                    if (response.data) {
                        const product = new Product(response.data);
                        this.updateProductList(product);
                        this.currencyService.setCurrentCurrency(
                            product.currency
                        );
                        return product;
                    }
                    return null;
                }),
                catchError(this.handleProductError(null))
            );
    }

        public getProductRedirectUrl(productId: number): Observable<string> {
        // const url = `${API_URL}${BASE_URL}/${productId}`;
            const url = `${this.apiURL}/${productId}`;

        return this.http.get<Response>(url)
            .pipe(
                map(response => {
                    return response['redirect_url'];
                }),
                catchError(this.handleProductError(null))
            );
    }


    protected handleProductError<T>(result?: T) {
        return (error: DetailedError & { error?: { code?: number } }): Observable<T> => {
            console.log('error', error);


            if (error?.error?.code === PRODUCT_ACCESS_ERROR) {
                this.toastr.error(error?.error?.message || 'You have no access to the product.');
            } else {
                this.router.navigateByUrl('/404', {skipLocationChange: true});
            }
            return of(result as T);
        };
    }

    getProducts(windowID: number, slugOrID: string | number = null, filter: Array<string> = null, paging: string = '18-0', sort: string,
                wslrEntityId = 0, refetchAggregatedQuantities = false): Observable<ProductResult> {

        if (!sort) {
            sort = 'is_featured DESC';
        }

        return this.getProductsIds(windowID, slugOrID, filter, paging, sort, wslrEntityId).pipe(
            switchMap( ({ids, count}) => {
                return this.getProductsByIds(ids).pipe(
                    switchMap( (products) => {
                       if (refetchAggregatedQuantities) {
                           return this.refreshAggregateQuantities(products, windowID)
                       }
                       return of(products);
                    }),
                    tap( products => {
                       if (!isEmptyArray(products)) {
                           this.currencyService.setCurrentCurrency(products[0].currency);
                       }
                    }),
                    map( products => {
                        return {products, count}
                }))
            })
        )

    }


    public getProductsShortInfo(windowID: number, slugOrID: string | number = null, orderId: number = 0, filter: Array<string> = null,
                                paging: string = '18-0', sort: string, show: string = '',
                                wslrEntityId = 0, refetchAggregatedQuantities = false,
                                includeOutOfStock = false): Observable<ProductResult> {
        if (!sort) {
            sort = 'is_featured DESC';
        }
        return this.api.getProductsShortInfo(windowID, slugOrID, orderId, filter, paging, sort, show, wslrEntityId, includeOutOfStock).pipe(
            switchMap (retResult => {
                if (!isEmptyArray(retResult.products)) {
                    retResult.products.forEach(product => {
                        this.shortInfoProducts[product.id] = product;
                    });
                    // set currency
                    this.currencyService.setCurrentCurrency(retResult.products[0].currency);
                }
                if (!refetchAggregatedQuantities) {
                    return of (retResult)
                } else {
                    return this.refreshAggregateQuantitiesForShortInfo(retResult.products, windowID).pipe(
                        map( retProducts => {
                            return {products: retProducts, count: retResult.count};
                        })
                    )
                }
            })
        );
    }

    public exportOrderedProducts(type: 'xlsx' | 'pdf', windowID: number, slugOrID: string | number = null, filter: Array<string> = null,
                                sort: string,
                                wslrEntityId = 0): Observable<boolean> {
        if (!sort) {
            sort = 'is_featured DESC';
        }
        return this.api.exportOrderedProducts(type, windowID, slugOrID, filter, sort,  wslrEntityId);
    }


    public getFavoriteProducts(windowID: number, filter: Array<string> = null, paging: string = '18-0',
                                       sort: string = '', show: string = '', wslrEntityId = 0): Observable<ProductResult> {

        this.fetchingFavoriteProducts = true;

        return this.api.getFavoriteProducts(windowID, filter, paging, sort, show, wslrEntityId).pipe(
            tap( ({products}) => {
                if (!isEmptyArray(products)) {
                    this.googleAnalytics.viewItems(products);
                    this.currencyService.setCurrentCurrency(products[0].currency);
                }
            }),
            tap( ({products, count}) => {
                this.favoriteProducts = { ...this.favoriteProducts, [windowID]: { products, count} };
                if (!isEmptyArray(products)) {
                    products.forEach(product => {
                        this.shortInfoProducts[product.id] = product;
                    });
                }
                this.fetchingFavoriteProducts = false;
                // return {products, count}
            })
        );


    }

    public getFavoriteFilters(windowId = 0, filters: FilterGroup[] = []): Observable<FilterGroup[]> {

        let key = '' + windowId;
        if (!isEmptyArray(filters)) {
            key = `${windowId}-${computeHash(transformFilters(filters).join(''))}`;
        }

        if (this.favoriteFiltersCache.has(key)) {
            return of(this.favoriteFiltersCache.get(key).map(  i =>  new FilterGroup(i)));
        }
        return this.api.fetchFavoritesFilters(windowId, filters).pipe(
            tap( retFilters => {
                if (Array.isArray(retFilters)) {
                    this.favoriteFiltersCache.set(key, retFilters);
                }
            }));
    }

    private getProductsIds(windowID: number, slugOrID: string | number = null,
                           filter: Array<string> = null, paging: string = '18-0', sort: string = '', wslrEntityId = 0):
        Observable<{ids: number[]; count: number}> {

        const productKey: string = btoa(
            JSON.stringify({w: windowID, g: slugOrID, f: filter, p: paging, s: sort, wid: wslrEntityId}))
            .replace('=', '');
        const productKeyCount = `${productKey}-count`;

        if (this.productCache.has(productKey)) {
            const ids: number[] = this.productCache.get(productKey);
            const count: number = this.productCache.get(productKeyCount);
            return of({ids, count});
        }
        return this.api.getProductsIds(windowID, slugOrID, filter, paging, sort, wslrEntityId).pipe( tap(({ids, count}) => {
            this.productCache.set(productKey, ids);
            this.productCache.set(productKeyCount, count);
        }));

    }

    /**
     * Fetches products using the requested response shape.
     *
     * Full products are stored in both caches because a full product can render
     * a card. Brief products are stored only in the brief cache and therefore
     * cannot satisfy a full-fidelity caller.
     */
    public getProductsByIds(
        ids: number[],
        options: GetProductsByIdsOptions = {}
    ): Observable<Product[]> {
        if (isEmptyArray(ids)) {
            return of([]);
        }

        const {
            force = false,
            shape = 'full',
        } = options;

        const isBrief = shape === 'brief';

        const getCache = (): Record<number, Product> =>
            isBrief ? this.briefProducts : this.products;

        const getCachedProducts = (): Product[] => {
            const currentCache = getCache();

            return ids
                .map(id => currentCache[id])
                .filter(
                    (product): product is Product =>
                        product !== undefined
                );
        };

        const currentCache = getCache();

        const missingIds = force
            ? [...ids]
            : ids.filter(id => currentCache[id] === undefined);

        if (missingIds.length === 0) {
            return of(getCachedProducts());
        }

        return this.api.getProductsByIds(missingIds, shape).pipe(
            tap(products => {
                products.forEach(product => {
                    if (!isBrief) {
                        this.products[product.id] = product;
                        this.briefProducts[product.id] = product;
                        return;
                    }

                    this.briefProducts[product.id] =
                        this.products[product.id] ?? product;
                });
            }),
            map(() => {
                return getCachedProducts();
            })
        );
    }

    /**
     *
     * @param productIds - array of product ids
     * @param windowID
     */
    public refreshAggregateQuantities(products: Product[], windowID: number): Observable<Product[]> {

        const productIdList = products.map( p => p.id);

        return this.api.refreshAggregateQuantities(productIdList, windowID).pipe(
            map( response => {
                if (!response) {
                    return products;
                }
                const productIds = Object.keys(response);
                for ( const id of productIds) {

                    const product = products.find( p => p.id === +id);
                    if (product) {
                        const newSkus = response[id];
                        if (product.skus && newSkus) {
                            product.skus.forEach(sku => {
                                const newSku = newSkus.find(s => s.sku_id === sku.id);
                                if (newSku) {
                                    sku.aggregated_quantity = newSku.sku;
                                    sku.group_aggregated_quantity = newSku.group || 0;
                                }
                            });
                        }
                    }
                }
                return products;
            })
        );

    }

    private refreshAggregateQuantitiesForShortInfo(products: Product[], windowID: number): Observable<Product[]> {

        const productIdList = products.map( p => p.id);

        return this.api.refreshAggregateQuantities(productIdList, windowID).pipe(
            map( response => {
                if (!response) {
                    return products;
                }
                const productIds = Object.keys(response);
                for ( const id of productIds) {

                    const product = products.find( p => p.id === +id);
                    if (product) {
                        const newSkus = response[id];
                        if (product.skus && newSkus) {
                            product.skus.forEach(sku => {
                                const newSku = newSkus.find(s => s.sku_id === sku.id);
                                if (newSku) {
                                    sku.aggregated_quantity = newSku.sku;
                                    sku.group_aggregated_quantity = newSku.group || 0;
                                }
                            });
                        }
                    }
                }
                return products;
            })
        );

    }

    // refresh the product in cache after an order-item change. pass freshProduct when the caller
    // already has a trustworthy product (create/update response); omit it to force a /for-ids
    // refetch (delete - the deleted item's product is now stale). returns a background,
    // failure-tolerant side-stream that emits nothing, to be merged into the caller's result.
    public refreshProductCacheForOrderItem(
        productId: number,
        freshProduct?: Product
    ): Observable<never> {
        if (freshProduct) {
            this.updateProductList(freshProduct);
            return EMPTY;
        }

        if (productId == null) {
            return EMPTY;
        }

        return this.getProductsByIds(
            [productId],
            {
                force: true,
                shape: 'full',
            }
        ).pipe(
            catchError(() => EMPTY),
            ignoreElements()
        );
    }

    public searchDecisionPoints(term: string): Observable<IDecisionPointData[]> {

        const key = term + '-0-0';

        if (this.dpCache.has(key)) {
            return of(this.dpCache.get(key));
        }

        return this.api.getDecisionPointData(term, '0', 0).pipe(
            map( data => {
                return data;
            }),
            tap( result => {
                if (!isEmptyArray(result)) {
                    this.dpCache.set(key, result);
                }
            })
        );

    }


    public getDecisionPointData(dpID: string, orderItem: OrderItem): Observable<IDecisionPointData> {
        const product = orderItem.product;
        const productId = product ? product.id : 0;
        return this.api.getDecisionPointData('', dpID, productId).pipe(
            map( data => {
                if (data && data.length === 1) {
                    const result = data[0];
                    if (product.inventoryType) {
                        result.availableQty = product.skus[0].available_quantity + orderItem.init_quantity;
                        result.dpQuantity = (result.availableQty > result.store_count) ? result.store_count : result.availableQty;
                    } else {
                        result.availableQty = -1;
                        result.dpQuantity = result.store_count;
                    }

                    result.orderItemQuantity = orderItem.quantity || 0;
                    return result;
                }
                return null;
            }),
        );
    }

    calculateDecisionPoints(dpID: string, productID: number, quantity: number): Observable<IDecisionPointDelivery[]> {
        return this.api.calculateDecisionPoints(dpID, productID, quantity);
    }


    public onActiveOrderWindowChanged(orderWindowId = 0) {
        if (orderWindowId > 0) {
            this.activeBuyingWindowId = orderWindowId;
        }
        
        this.buyingWindowCatalogs = [];

        return forkJoin([
            this.getStatistics(orderWindowId),
            this.getCatalogsForHomepage(orderWindowId, PROGRAMS_PAGING),
        ]);
    }


    getHeroSlides(orderWindow: OrderWindow): Observable<HeroSlide[]> {
        if (!orderWindow) {
            return of([]);
        }

        return this.getCatalogs(orderWindow.id, null, '5-0', 'is_featured DESC').pipe(
            map( ({catalogs}) => {

                const result = [];
                if (Array.isArray(catalogs)) {

                    catalogs.forEach( catalog => {
                        if (catalog.featureType === CAROUSEL_TYPE) {
                            const img = catalog.getHeroImage();
                            if (img) {
                                result.push({
                                    image: img['url'],
                                    headline: catalog.label,
                                    description: '',
                                    url: catalog.programDetailsUrl,
                                });
                            }
                        }

                    });
                }

                return result;
            })
        );
    }

    setStatistics(statistics: ProductStatistics) {
        this.statistics = statistics;
    }

    reset(): void {
        this.products = {};
        this.briefProducts = {};
        this.shortInfoProducts = {};
        this.productCatalogs = {};
        this.api.reset();
        this.catalogCache.clear();
        this.productCache.clear();

        this.statistics = {};
        this.onDemandCatalogs = [];
        this.buyingWindowCatalogs = [];
    }

    get heroSlides()  {
        return [...this.buyingWindowHeroSlides, ...this.onDemandHeroSlides].slice(0, 5);
    }

    public toggleFavorites(product: Product): Observable<Product> {
        if (!product?.id) {
            return of(product);
        }

        return this.api.toggleFavorite(product).pipe(
            tap(retProduct => {
                if (retProduct) {

                    // retProduct may be short-info shaped; only overwrite products with a Full clone.
                    if (retProduct.productView === ProductView.Full) {
                        this.products[retProduct.id] = retProduct; // update cached product
                    }
                    this.updatedProduct.next(retProduct);

                    const briefProduct = this.briefProducts[retProduct.id];
                    if (briefProduct) {
                        briefProduct.setFavorite(retProduct.isFavorite);
                        this.updatedBriefProduct.next(briefProduct);
                    }

                    const shortInfoProduct = this.shortInfoProducts[retProduct.id];
                    if (shortInfoProduct) {
                        shortInfoProduct.setFavorite(retProduct.isFavorite);
                        this.updatedShortInfoProduct.next(shortInfoProduct);
                    }

                    if (!isEmptyArray(this.onDemandPopularProducts)) {
                        this.onDemandPopularProducts = this.onDemandPopularProducts.map(p => {
                            if (p.id === retProduct.id) {
                                p.setFavorite(retProduct.isFavorite);
                            }
                            return p;
                        })
                    }

                    const msg = retProduct.label +
                        (retProduct.isFavorite ? ' has been added to Favorites' : ' has been removed from Favorites');
                    this.toastr.success(msg, null, {enableHtml: true});
                    this.updateFavoriteProductsInCache(retProduct);
                }
            })
        );
    }

    public getApiUrl(): string {
        return this.apiURL;
    }

    public populateProduct(product: Product) {
        if (product) {
            const url = `${API_URL}/increment-popularity/product/${product.id}`;
            this.http.post<Response>(url, {}).subscribe();
        }
    }

    public updateProductList(product: Product): void {
        if (!product?.id) {
            return;
        }

        this.products[product.id] = product;
        this.briefProducts[product.id] = product;
    }

    public updateProductFromOrder(order: Order) {
        if (!order || isEmptyArray(order.items)) {
            return;
        }

        // get list of products from order
        const products = order.items.map( item => {
            if (item.product && item.product.id) {
                return item.product;
            }
            return null;
        }).filter( p => p !== null);

        if (isEmptyArray(products)) {
            return;
        }
        // update products in cache
        products.forEach( product => {
            this.products[product.id] = product;
            this.briefProducts[product.id] = product;
        });
    }

    public validateProduct(product: Product, buyingWindow: OrderWindow = null): Observable<boolean> {
        if (!product) {
            return of(null);
        }
        const orderWindowId = buyingWindow?.id || 0;
        return this.api.validateProduct(product.id, orderWindowId);
    }

    public fetchRecentProducts(
        filter: Array<string> = null,
        paging = '18-0',
        sort = ''
    ): Observable<Product[]> {
        this.fetchingRecentPurchasedProducts = true;

        return this.api.fetchRecentProducts(filter, paging, sort).pipe(
            switchMap(({ids, count}) => {
                return this.getProductsByIds(
                    ids,
                    {shape: 'brief'}
                ).pipe(
                    tap(products => {
                        if (!isEmptyArray(products)) {
                            this.currencyService.setCurrentCurrency(
                                products[0].currency
                            );
                        }

                        this.onDemandRecentPurchasesProducts = products;
                        this.onDemandRecentPurchasesProductsTotalCount = count;
                    })
                );
            }),
            finalize(() => {
                this.fetchingRecentPurchasedProducts = false;
            })
        );
    }

    public get onDemandHeroSlides() {
        return convertCatalogsToHeroSlides(this.onDemandCatalogs);
    }

    public get buyingWindowHeroSlides() {
        return convertCatalogsToHeroSlides(this.buyingWindowCatalogs);
    }

    private sortCatalogsByLabel(catalogs: ProductCatalog[]): ProductCatalog[] {
        return catalogs.sort( (c1, c2) => c1.label.localeCompare(c2.label));
    }

    private updateFavoriteProductsInCache(product: Product) {
        const windowId = product.window_id || 0;
        const isFavorite = product.isFavorite;

        const products =  this.favoriteProducts[windowId]?.products || [];
        const count = this.favoriteProducts[windowId]?.count || 0;
        const favorites = {...this.favoriteProducts};

        if (!isFavorite) {
            // remove from favorites cache

            favorites[windowId] = {products: products.filter( p => p.id !== product.id), count: count - 1};
        } else {
            // add to to favorites cache
            if (!products.some( p => p.id === product.id)) {
                favorites[windowId] = {products: [...products, product], count: count + 1};
            }
        }
        this.favoriteProducts = favorites;
    }

    private setCatalogs(windowId, catalogs: ProductCatalog[] = []) {
        if (windowId) {
            this.buyingWindowCatalogs = [...catalogs];
        } else {
            this.onDemandCatalogs = [...catalogs];
        }
    }

    private setHomepageCatalogs(windowId, catalogs: ProductCatalog[] = []) {
        if (windowId) {
            this.buyingWindowHomepageCatalogs = [...catalogs];
        } else {
            this.onDemandHomepageCatalogs = [...catalogs];
        }
    }

    public fetchOnDemandProductCategories(): Observable<ProductCategory[]> {
        if (!isEmptyArray(this.onDemandProductCategories)) {
            return of(this.onDemandProductCategories);
        }

        this.fetchingOnDemandProductCategories = true;
        return this.api.fetchProductCategories().pipe(
            tap( result => {
                this.onDemandProductCategories = result || [];
                this.fetchingOnDemandProductCategories = false;
            })
        );
    }

    public fetchOnDemandProductBrands(): Observable<Brand[]> {
        if (!isEmptyArray(this.onDemandProductBrands)) {
            return of(this.onDemandProductBrands);
        }

        this.fetchingOnDemandProductBrands = true;

        return this.api.fetchProductBrands().pipe(
            tap( result => {
                this.onDemandProductBrands = result || [];
                this.fetchingOnDemandProductBrands = false;
            })
        );
    }


    get onDemandFavorites(): Product[] {
        const firstKey = Object.keys(this.favoriteProducts)[0];
        return this.favoriteProducts[firstKey]?.products || [];
    }

    public populatedUpdatedProduct(updatedProduct: Product) {
        if (!updatedProduct) {
            return;
        }
        this.products[updatedProduct.id] = updatedProduct; // update cached product
        this.updatedProduct.next(updatedProduct);

        this.briefProducts[updatedProduct.id] = updatedProduct;
        this.updatedBriefProduct.next(updatedProduct);

        // Listing/favourites rows also need this update.
        if (this.shortInfoProducts[updatedProduct.id]) {
            this.shortInfoProducts[updatedProduct.id] = updatedProduct;
            this.updatedShortInfoProduct.next(updatedProduct);
        }
    }


    public fetchAllocationGroups(): Observable<AllocationGroup[]> {
        return this.store.select(selectUserAllocationGroupsLoaded).pipe(
            take(1),
            switchMap(loaded => {
                if (loaded) {
                    return this.store.select(selectUserAllocationGroups).pipe(take(1));
                }
                return this.fetchAllocationGroupsOnce$();
            }),
        );
    }

    /**
     * The `loaded` flag only flips once the response is back, so every caller subscribing before that
     * would fire its own request - and there is one caller per allocation group control on the screen.
     * Callers that arrive while a request is in flight share it instead.
     * Emits once and completes, like the request it wraps: callers rely on that (see getInventoryAllocationForProduct).
     */
    private fetchAllocationGroupsOnce$(): Observable<AllocationGroup[]> {
        if (!this.inFlightAllocationGroups$) {
            this.store.dispatch(fetchUserAllocationGroups());
            this.inFlightAllocationGroups$ = this.api.fetchAllocationGroups().pipe(
                map(groups => groups || []),
                tap(groups => this.store.dispatch(fetchUserAllocationGroupsSuccess({ groups }))),
                catchError(error => {
                    this.store.dispatch(fetchUserAllocationGroupsFailure());
                    return this.handleError('ProductService::fetchAllocationGroups', [] as AllocationGroup[])(error);
                }),
                finalize(() => this.inFlightAllocationGroups$ = null),
                shareReplay(1),
            );
        }

        return this.inFlightAllocationGroups$;
    }

    public getInventoryAllocationForProduct(product: Product) {
        if (!product || product.isBuyingWindow === true || product.isEnabledAllocatedInventory === false) {
            if (product?.id) {
                this.store.dispatch(resetProductAllocations({ productId: product.id }));
            }
            return;
        }

        const productId = product.id;
        this.store.dispatch(fetchProductAllocations({ productId }));

        this.fetchInventoryAllocationForProduct$(product).subscribe({
            next: (allocations: InventoryAllocation[]) => {
                this.store.dispatch(fetchProductAllocationsSuccess({ productId, allocations }));
            },
            error: () => {
                this.store.dispatch(fetchProductAllocationsFailure({ productId }));
            },
        });
    }

    public fetchInventoryAllocationForProduct$(product: Product): Observable<InventoryAllocation[]> {
        if (!product || product.isBuyingWindow === true || product.isEnabledAllocatedInventory === false) {
            return of([]);
        }

        return this.fetchAllocationGroups().pipe(
            switchMap( allocationGroups => {
                if (isEmptyArray(allocationGroups)) {
                    return of([] as InventoryAllocation[]);
                }

                return this.api.fetchAllocationGroupForProduct(product.id).pipe(
                  map(availableQuantityGroups => {
                    if (isEmptyArray(availableQuantityGroups)) {
                        return [] as InventoryAllocation[];
                    }

                    return this.withAllocationGroupLabels(availableQuantityGroups, allocationGroups);
                  }));
            }));
    }

    /**
     * The list view counterpart of getInventoryAllocationForProduct: a page of the catalog holds as
     * many allocated inventory rows as it likes, and every one of them needs its groups before the
     * user can configure it. Asking per row would be one request per visible product.
     */
    public getInventoryAllocationForProducts(products: Product[]) {
        const candidates = allocatedInventoryProducts(products);

        if (isEmptyArray(candidates)) {
            return;
        }

        this.store.select(selectProductIdsNeedingAllocations(candidates.map(product => product.id)))
            .pipe(take(1))
            .subscribe(productIds => {
                if (isEmptyArray(productIds)) {
                    // already loaded or already in flight - the products setter of the list view hands
                    // back the whole grown array on every page, not just what it added
                    return;
                }

                this.store.dispatch(fetchProductsAllocations({ productIds }));

                this.fetchInventoryAllocationForProducts$(productIds).subscribe({
                    next: (allocationsByProductId: Record<number, InventoryAllocation[]>) => {
                        this.store.dispatch(fetchProductsAllocationsSuccess({ allocationsByProductId }));
                    },
                    error: () => {
                        this.store.dispatch(fetchProductsAllocationsFailure({ productIds }));
                    },
                });
            });
    }

    /**
     * An address that carried ordered products releases what it reserved the moment it is removed or
     * re-pointed, so every Available figure the page shows is out of date from then on. The store
     * keeps a product once it is loaded - selectProductIdsNeedingAllocations answers "needs fetching"
     * only for one it has never seen - so the entries have to be dropped before the batch fetch will
     * ask for them again.
     */
    public refreshInventoryAllocationsForProducts(products: Product[]) {
        const candidates = allocatedInventoryProducts(products);

        if (isEmptyArray(candidates)) {
            return;
        }

        candidates.forEach(product => this.store.dispatch(resetProductAllocations({ productId: product.id })));
        this.getInventoryAllocationForProducts(candidates);
    }

    /**
     * Emits one entry per requested product, empty ones included - see fetchProductsAllocationsSuccess
     * for why a product the response omits still has to be written to the store.
     */
    public fetchInventoryAllocationForProducts$(productIds: number[]): Observable<Record<number, InventoryAllocation[]>> {
        if (isEmptyArray(productIds)) {
            return of({});
        }

        const emptyResult = () => productIds.reduce((byProductId, productId) => {
            byProductId[productId] = [] as InventoryAllocation[];
            return byProductId;
        }, {} as Record<number, InventoryAllocation[]>);

        return this.fetchAllocationGroups().pipe(
            switchMap(allocationGroups => {
                if (isEmptyArray(allocationGroups)) {
                    // the user has no allocation groups at all: nothing to offer for any of them
                    return of(emptyResult());
                }

                return this.api.fetchAllocationGroupForProducts(productIds).pipe(
                    map(allocationsByProductId => productIds.reduce((result, productId) => {
                        result[productId] = this.withAllocationGroupLabels(
                            allocationsByProductId?.[productId], allocationGroups);
                        return result;
                    }, {} as Record<number, InventoryAllocation[]>)));
            }));
    }

    /**
     * The product endpoints return allocation group ids and quantities only - the labels live on the
     * user's own groups. Shared by the single product and batch paths so one group cannot end up
     * labelled one way in grid view and another in list view.
     *
     * Builds fresh objects: the incoming array may be frozen (e.g. already in the store from an
     * earlier fetch) and ngrx strict-immutability throws on mutation.
     */
    private withAllocationGroupLabels(
        allocations: InventoryAllocation[],
        allocationGroups: AllocationGroup[]): InventoryAllocation[] {

        return (allocations || []).map(group => ({
            ...group,
            allocation_qty: (group.allocation_qty || []).map(aq => {
                const allocGroup = allocationGroups.find(g => g.allocation_group_id === aq.allocation_group_id);
                return allocGroup
                    ? { ...aq, allocation_group_label: allocGroup.allocation_group_label }
                    : { ...aq };
            }),
        }));
    }

    public getInventoryAllocationForSku(productId: number, skuId?: number): Observable<InventoryAllocation> {
        return this.store.select(selectInventoryAllocationForSku(productId, skuId));
    }

    public allInventoryAllocationsDefined$(productId: number): Observable<boolean> {
        return this.store.select(selectAllProductAllocationsDefined(productId));
    }

    public computeTotalAvailableQuantity$(productId: number, skuId?: number): Observable<{inStock: boolean, totalQty: number}> {
        return this.store.select(selectTotalAvailableQuantity(productId, skuId));
    }


    public getAllocationGroupById(allocationGroupId: number): Observable<AllocationGroup> {
      if (!allocationGroupId) {
        return of(null);
      }

      return this.fetchAllocationGroups().pipe(
        map(groups => groups.find(g => g.allocation_group_id === allocationGroupId) || null),
      );
    }

    public someVariationsHasAllocationGroups(productId: number): Observable<boolean> {
        return this.store.select(selectSomeVariationsHasAllocationGroups(productId));
    }

    public variationsHasInventoryAllocations(productId: number, skuId: number): Observable<boolean> {
        return this.store.select(selectVariationHasInventoryAllocations(productId, skuId));
    }

    public isInventoryAllocationOutOfStock(productId: number, skuId?: number): Observable<boolean> {
        return this.store.select(selectIsInventoryAllocationOutOfStock(productId, skuId));
    }

    public isProductInventoryAllocationOutOfStock(productId: number): Observable<boolean> {
        return this.store.select(selectIsProductInventoryAllocationOutOfStock(productId));
    }

    public isFetchingProductAllocations$(productId: number): Observable<boolean> {
        return this.store.select(selectIsFetchingProductAllocations(productId));
    }

    /** Whether the last allocations request for this product failed, as opposed to returning nothing. */
    public isProductAllocationsFailed$(productId: number): Observable<boolean> {
        return this.store.select(selectProductAllocationsFailed(productId));
    }

    public isProductAllocationsLoaded$(productId: number): Observable<boolean> {
        return this.store.select(selectProductAllocationsLoaded(productId));
    }
}
